package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.browser.window
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.StyleScope
import se.mittpass.shared.api.*

private val reportScope = MainScope()

@Composable
fun FoodSafetyReportPage(workplaceId: Long) {
    var report by remember { mutableStateOf<FoodSafetyReportDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var fromDate by remember { mutableStateOf(addReportDays(-30)) }
    var toDate by remember { mutableStateOf(todayReport()) }
    var isInspectorView by remember { mutableStateOf(false) } // Default to visual graphical dashboard view
    var showOnlyDeviations by remember { mutableStateOf(false) }

    fun load() { loading = true; reportScope.launch { foodSafetyRepo.getReport(workplaceId, fromDate, toDate).onSuccess { report = it }; loading = false } }
    LaunchedEffect(workplaceId) { load() }

    fun downloadCsv() {
        val r = report ?: return
        val sb = StringBuilder()
        sb.append("Tidsstämpel;Mätpunkt/Kontroll;Kategori;Börvärde/Gräns;Uppmätt värde;Status;Signerad av;Korrigerande åtgärd\n")
        r.deviations.forEach { dev ->
            val valStr = dev.valueNumeric?.let { "$it°C" } ?: if (dev.valueOk == true) "OK" else "Ej OK"
            val action = dev.deviationAction?.replace(";", ",") ?: "Ingen åtgärd"
            sb.append("${formatReportDate(dev.loggedAt)};${dev.templateName};Avvikelse;—;${valStr};AVVIKELSE;${dev.loggedByName};\"${action}\"\n")
        }
        val blob = org.w3c.files.Blob(arrayOf(sb.toString()), org.w3c.files.BlobPropertyBag(type = "text/csv;charset=utf-8;"))
        val url = org.w3c.dom.url.URL.createObjectURL(blob)
        val link = kotlinx.browser.document.createElement("a") as org.w3c.dom.HTMLAnchorElement
        link.href = url
        link.download = "mittpass_haccp_inspektion_$fromDate.csv"
        link.click()
    }

    Div({ classes("print-container"); style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {
        // Print-Only Official Document Header (Livsmedelsverket & Miljö/Hälsa standard)
        Div({
            classes("print-only")
            style {
                property("padding", "0.75rem 0 1rem 0")
                property("border-bottom", "2px solid var(--rp-text)")
                property("margin-bottom", "0.75rem")
            }
        }) {
            Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "flex-end") } }) {
                Div {
                    H1({ style { property("font-size", "1.4rem"); property("font-weight", "900"); property("margin", "0"); property("color", "var(--rp-text)"); property("letter-spacing", "-0.3px") } }) { Text("HACCP EGENKONTROLL — INSPEKTIONSLOGG") }
                    P({ style { property("margin", "0.2rem 0 0 0"); property("color", "var(--rp-text)"); property("font-size", "0.82rem"); property("font-weight", "600") } }) { Text("Officiell myndighetsjournal för Livsmedelsverket & Kommunal Miljö- och Hälsoskyddsnämnd") }
                }
                Div({ style { property("text-align", "right"); property("font-size", "0.8rem"); property("color", "var(--rp-text)"); property("line-height", "1.35") } }) {
                    Div { Span({ style { property("font-weight", "800") } }) { Text("Kontrollplats: ") }; Text("Arbetsplats #$workplaceId") }
                    Div { Span({ style { property("font-weight", "800") } }) { Text("Period: ") }; Text("$fromDate till $toDate") }
                    Div { Span({ style { property("font-weight", "800") } }) { Text("Utskriven: ") }; Text(todayReport()) }
                }
            }
        }

        // Screen-Only Header & Control Bar
        Div({
            classes("no-print")
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "16px")
                property("padding", "1.1rem 1.3rem")
                property("border", "1px solid var(--rp-hl-med)")
                property("box-shadow", "0 4px 12px rgba(0,0,0,0.03)")
                property("display", "flex")
                property("justify-content", "space-between")
                property("align-items", "center")
                property("flex-wrap", "wrap")
                property("gap", "1rem")
            }
        }) {
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.85rem") } }) {
                Div({
                    style {
                        property("width", "40px"); property("height", "40px"); property("border-radius", "12px")
                        property("background", if (isInspectorView) "var(--rp-pine)" else "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    }
                }) {
                    FaIcon(if (isInspectorView) "fa-solid fa-list-check" else "fa-solid fa-chart-pie", size = "1.1rem", color = "white")
                }
                Div {
                    H2({ style { property("margin", "0 0 0.15rem 0"); property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                        Text(if (isInspectorView) "Inspektörsvy — Komprimerad Logg" else "Visuell Rapportsida")
                    }
                    P({ style { property("margin", "0"); property("color", "var(--rp-subtle)"); property("font-size", "0.82rem"); property("font-weight", "500") } }) {
                        Text(if (isInspectorView) "Ren, rak och tät granskningslista för Miljö & Hälsa utan grafik" else "Interaktiv visuell översikt för caféchef")
                    }
                }
            }

            // Controls & Filters
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.6rem"); property("flex-wrap", "wrap") } }) {
                // View Switcher Toggle
                Button({
                    onClick { isInspectorView = !isInspectorView }
                    style {
                        property("padding", "0.45rem 0.95rem")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("background", if (isInspectorView) "var(--rp-overlay)" else "var(--rp-surface)")
                        property("color", "var(--rp-text)")
                        property("border-radius", "8px")
                        property("font-weight", "700")
                        property("font-size", "0.8rem")
                        property("cursor", "pointer")
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("gap", "0.4rem")
                    }
                }) {
                    FaIcon(if (isInspectorView) "fa-solid fa-chart-pie" else "fa-solid fa-list-check", size = "0.8rem")
                    Text(if (isInspectorView) "Visa grafisk översikt" else "Komprimerad inspektörslista")
                }

                if (isInspectorView) {
                    Button({
                        onClick { showOnlyDeviations = !showOnlyDeviations }
                        style {
                            property("padding", "0.45rem 0.95rem")
                            property("border", if (showOnlyDeviations) "1.5px solid var(--rp-err-border)" else "1px solid var(--rp-hl-med)")
                            property("background", if (showOnlyDeviations) "var(--rp-err-bg)" else "var(--rp-surface)")
                            property("color", if (showOnlyDeviations) "var(--rp-err-text)" else "var(--rp-text)")
                            property("border-radius", "8px")
                            property("font-weight", "700")
                            property("font-size", "0.8rem")
                            property("cursor", "pointer")
                            property("display", "inline-flex")
                            property("align-items", "center")
                            property("gap", "0.4rem")
                        }
                    }) {
                        FaIcon("fa-solid fa-triangle-exclamation", size = "0.8rem", color = if (showOnlyDeviations) "var(--rp-err-text)" else "var(--rp-subtle)")
                        Text(if (showOnlyDeviations) "Endast avvikelser (Aktiv)" else "Visa endast avvikelser")
                    }
                }

                Span({ style { property("font-weight", "700"); property("font-size", "0.8rem"); property("color", "var(--rp-subtle)"); property("margin-left", "0.3rem") } }) { Text("Period:") }
                Input(InputType.Date) { value(fromDate); onInput { fromDate = it.value }; style { rptInput() } }
                Span({ style { property("color", "var(--rp-muted)") } }) { Text("–") }
                Input(InputType.Date) { value(toDate); onInput { toDate = it.value }; style { rptInput() } }
                Button({
                    onClick { load() }
                    style {
                        property("padding", "0.45rem 0.9rem")
                        property("border", "none")
                        property("background", "var(--rp-pine)")
                        property("color", "white")
                        property("border-radius", "8px")
                        property("font-weight", "700")
                        property("font-size", "0.8rem")
                        property("cursor", "pointer")
                    }
                }) { Text("Uppdatera") }

                Button({
                    onClick { downloadCsv() }
                    style {
                        property("padding", "0.45rem 0.9rem")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("background", "var(--rp-surface)")
                        property("color", "var(--rp-text)")
                        property("border-radius", "8px")
                        property("font-weight", "700")
                        property("font-size", "0.8rem")
                        property("cursor", "pointer")
                    }
                }) {
                    FaIcon("fa-solid fa-file-csv", size = "0.85rem", color = "var(--rp-ok-text)")
                    Text(" Exportera CSV")
                }

                Button({
                    onClick { window.print() }
                    style {
                        property("padding", "0.45rem 1rem")
                        property("border", "none")
                        property("background", "var(--rp-pine)")
                        property("color", "white")
                        property("border-radius", "8px")
                        property("font-weight", "700")
                        property("font-size", "0.8rem")
                        property("cursor", "pointer")
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "0.4rem")
                    }
                }) {
                    FaIcon("fa-solid fa-print", size = "0.85rem", color = "white")
                    Text("Skriv ut A4")
                }
            }
        }

        if (loading || report == null) {
            Div({ style { property("text-align", "center"); property("padding", "3rem"); property("color", "var(--rp-subtle)"); property("font-weight", "600") } }) {
                FaIcon("fa-solid fa-spinner fa-spin", size = "1.3rem", color = "var(--rp-text)")
                Br(); Br()
                Text("Laddar inspektionsjournal…")
            }
            return@Div
        }

        val r = report!!

        if (isInspectorView) {
            // ── COMPRESSED INSPECTOR TABULAR VIEW (Zero Lull-Lull) ──
            Div({
                style {
                    property("background", "var(--rp-surface)")
                    property("border", "1px solid var(--rp-text)")
                    property("border-radius", "0px")
                    property("padding", "0.5rem")
                }
            }) {
                // Meta Bar
                Div({
                    style {
                        property("display", "flex")
                        property("justify-content", "space-between")
                        property("border-bottom", "1.5px solid var(--rp-text)")
                        property("padding-bottom", "0.4rem")
                        property("margin-bottom", "0.5rem")
                        property("font-size", "0.8rem")
                        property("font-weight", "700")
                    }
                }) {
                    Span { Text("TOTALT UTFÖRDA KONTROLLER: ${r.totalCompleted} st (${r.compliancePercent.toInt()}% EFTERLEVNAD)") }
                    Span({ style { property("color", if (r.deviationCount > 0) "var(--rp-err-text)" else "var(--rp-text)") } }) {
                        Text("AVVIKELSER: ${r.deviationCount} ST (${r.deviationsWithAction} ÅTGÄRDADE)")
                    }
                }

                // Ultra-Dense Chronological Log Table
                Table({
                    style {
                        property("width", "100%")
                        property("border-collapse", "collapse")
                        property("font-size", "0.78rem")
                        property("font-family", "system-ui, sans-serif")
                    }
                }) {
                    Thead {
                        Tr({ style { property("background", "var(--rp-overlay)"); property("border-bottom", "2px solid var(--rp-pine)") } }) {
                            listOf("Datum & Tid", "Mätpunkt / Rutin", "Gräns", "Uppmätt Värde", "Status", "Signerad Av", "Vidtagen Åtgärd / Anteckning").forEach { h ->
                                Th({
                                    style {
                                        property("text-align", "left")
                                        property("padding", "0.35rem 0.5rem")
                                        property("font-weight", "900")
                                        property("color", "var(--rp-text)")
                                        property("border", "1px solid var(--rp-hl-med)")
                                        property("text-transform", "uppercase")
                                        property("font-size", "0.72rem")
                                    }
                                }) { Text(h) }
                            }
                        }
                    }
                    Tbody {
                        val allLogs = (r.recentLogs + r.deviations).distinctBy { it.id }.sortedByDescending { it.loggedAt }
                        val displayLogs = if (showOnlyDeviations) allLogs.filter { it.isDeviation || it.valueOk == false } else allLogs

                        if (displayLogs.isEmpty()) {
                            Tr {
                                Td({ attr("colspan", "7"); style { property("padding", "1rem"); property("text-align", "center"); property("color", "var(--rp-subtle)"); property("font-weight", "700") } }) {
                                    Text(if (showOnlyDeviations) "Inga avvikelser registrerade under vald period." else "Inga inrapporterade mätvärden under vald period.")
                                }
                            }
                        } else {
                            displayLogs.forEach { log ->
                                val tmpl = r.templates.find { it.id == log.templateId }
                                val isDev = log.isDeviation || log.valueOk == false
                                Tr({
                                    style {
                                        if (isDev) {
                                            property("background", "var(--rp-err-bg)")
                                            property("border-bottom", "1px solid var(--rp-err-border)")
                                        } else {
                                            property("border-bottom", "1px solid var(--rp-hl-med)")
                                        }
                                    }
                                }) {
                                    Td({ style { denseCell(); property("font-weight", "800"); property("white-space", "nowrap") } }) { Text(formatReportDate(log.loggedAt)) }
                                    Td({ style { denseCell(); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text(log.templateName) }
                                    Td({ style { denseCell(); property("color", "var(--rp-subtle)") } }) {
                                        Text(if (tmpl?.minValue != null && tmpl?.maxValue != null) "${tmpl.minValue}°–${tmpl.maxValue}°C" else "Ja/Nej")
                                    }
                                    Td({ style { denseCell(); property("font-weight", "900"); property("color", if (isDev) "var(--rp-err-text)" else "var(--rp-ok-text)") } }) {
                                        Text(when {
                                            log.valueNumeric != null -> "${log.valueNumeric}°C"
                                            log.valueOk != null -> if (log.valueOk == true) "Godkänd (OK)" else "Ej OK"
                                            else -> log.valueText ?: "—"
                                        })
                                    }
                                    Td({ style { denseCell() } }) {
                                        Span({
                                            style {
                                                property("background", if (isDev) "var(--rp-love)" else "var(--rp-pine)")
                                                property("color", "white")
                                                property("padding", "0.1rem 0.4rem")
                                                property("font-weight", "900")
                                                property("font-size", "0.68rem")
                                                property("border-radius", "4px")
                                            }
                                        }) {
                                            Text(if (isDev) "AVVIKELSE" else "OK")
                                        }
                                    }
                                    Td({ style { denseCell(); property("font-weight", "700") } }) { Text(log.loggedByName) }
                                    Td({ style { denseCell(); property("color", "var(--rp-text)"); property("font-weight", if (isDev) "700" else "400") } }) {
                                        if (!log.deviationAction.isNullOrBlank()) {
                                            Text("Åtgärd: ${log.deviationAction}")
                                        } else if (isDev) {
                                            Span({ style { property("color", "var(--rp-err-text)"); property("font-weight", "800"); property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.3rem") } }) {
                                                FaIcon("fa-solid fa-triangle-exclamation", size = "0.75rem", color = "var(--rp-err-text)")
                                                Text("Saknar dokumenterad åtgärd")
                                            }
                                        } else {
                                            Text("—")
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        } else {
            // ── VISUAL DASHBOARD VIEW FOR CAFÉ MANAGER ──
            // KPI cards grid
            Div({ style { property("display", "grid"); property("grid-template-columns", "repeat(auto-fit, minmax(220px, 1fr))"); property("gap", "1rem") } }) {
                ReportKpiCard("Kontroller utförda", "${r.totalCompleted} / ${r.totalExpected}", if (r.compliancePercent >= 95) "var(--rp-ok-text)" else if (r.compliancePercent >= 85) "var(--rp-warn-text)" else "var(--rp-err-text)", "HACCP täckning")
                ReportKpiCard("Efterlevnad", "${r.compliancePercent.toInt()}%", if (r.compliancePercent >= 95) "var(--rp-ok-text)" else if (r.compliancePercent >= 85) "var(--rp-warn-text)" else "var(--rp-err-text)", "Efterlevnadsgrad")
                ReportKpiCard("Avvikelser", "${r.deviationCount}", if (r.deviationCount == 0) "var(--rp-ok-text)" else "var(--rp-err-text)", "Upptäckta avvikelser")
                ReportKpiCard("Åtgärdade avvikelser", "${r.deviationsWithAction} / ${r.deviationCount}", if (r.deviationCount == 0 || r.deviationsWithAction == r.deviationCount) "var(--rp-ok-text)" else "var(--rp-warn-text)", "Dokumenterade åtgärder")
            }

            // Active control points table
            Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "16px"); property("padding", "1.25rem"); property("border", "1px solid var(--rp-overlay)") } }) {
                H3({ style { property("margin", "0 0 1rem 0"); property("font-size", "1.1rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("Aktiva kontrollpunkter & gränsvärden") }
                Div({ style { property("overflow-x", "auto") } }) {
                    Table({ style { property("width", "100%"); property("border-collapse", "collapse"); property("font-size", "0.88rem") } }) {
                        Thead {
                            Tr {
                                listOf("Namn", "Kategori", "Enhet", "Min °C", "Max °C", "Frekvens").forEach { h ->
                                    Th({ style { property("text-align", "left"); property("padding", "0.6rem 0.85rem"); property("border-bottom", "2px solid var(--rp-hl-med)"); property("font-weight", "800"); property("color", "var(--rp-subtle)"); property("font-size", "0.75rem"); property("text-transform", "uppercase") } }) { Text(h) }
                                }
                            }
                        }
                        Tbody {
                            r.templates.filter { it.active }.forEach { tmpl ->
                                Tr {
                                    Td({ style { rptCell(); property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(tmpl.name) }
                                    Td({ style { rptCell() } }) {
                                        val catBadgeBg = when (tmpl.category) { "temperature" -> "var(--rp-info-bg)"; "cleaning" -> "var(--rp-ok-bg)"; "receiving" -> "var(--rp-info-bg)"; else -> "var(--rp-overlay)" }
                                        val catBadgeFg = when (tmpl.category) { "temperature" -> "var(--rp-pine)"; "cleaning" -> "var(--rp-ok-text)"; "receiving" -> "var(--rp-iris)"; else -> "var(--rp-subtle)" }
                                        Span({ style { property("background", catBadgeBg); property("color", catBadgeFg); property("padding", "0.2rem 0.6rem"); property("border-radius", "50px"); property("font-size", "0.72rem"); property("font-weight", "700") } }) {
                                            Text(mapOf("temperature" to "Temperatur", "cleaning" to "Rengöring", "receiving" to "Mottagning", "other" to "Övrigt")[tmpl.category] ?: tmpl.category)
                                        }
                                    }
                                    Td({ style { rptCell(); property("color", "var(--rp-subtle)") } }) { Text(if (tmpl.unit == "boolean") "Ja/Nej" else if (tmpl.unit == "C") "°C" else tmpl.unit) }
                                    Td({ style { rptCell(); property("font-weight", "700"); property("color", "var(--rp-pine)") } }) { Text(tmpl.minValue?.let { "$it°C" } ?: "—") }
                                    Td({ style { rptCell(); property("font-weight", "700"); property("color", "var(--rp-pine)") } }) { Text(tmpl.maxValue?.let { "$it°C" } ?: "—") }
                                    Td({ style { rptCell(); property("color", "var(--rp-subtle)") } }) { Text(mapOf("daily" to "Dagligen", "per_delivery" to "Per leverans", "weekly" to "Veckovis")[tmpl.frequency] ?: tmpl.frequency) }
                                }
                            }
                        }
                    }
                }
            }

            // Reported Log Entries Table in Dashboard View
            if (r.recentLogs.isNotEmpty()) {
                Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "16px"); property("padding", "1.25rem"); property("border", "1px solid var(--rp-overlay)") } }) {
                    H3({ style { property("margin", "0 0 1rem 0"); property("font-size", "1.1rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("Senaste inrapporterade mätvärden (${r.recentLogs.size} st)") }
                    Div({ style { property("overflow-x", "auto") } }) {
                        Table({ style { property("width", "100%"); property("border-collapse", "collapse"); property("font-size", "0.85rem") } }) {
                            Thead {
                                Tr {
                                    listOf("Tidstämpel", "Kontrollpunkt", "Uppmätt Värde", "Signerad Av", "Status / Åtgärd").forEach { h ->
                                        Th({ style { property("text-align", "left"); property("padding", "0.5rem 0.75rem"); property("border-bottom", "2px solid var(--rp-hl-med)"); property("font-weight", "800"); property("color", "var(--rp-subtle)"); property("font-size", "0.75rem"); property("text-transform", "uppercase") } }) { Text(h) }
                                    }
                                }
                            }
                            Tbody {
                                r.recentLogs.take(25).forEach { log ->
                                    val isDev = log.isDeviation || log.valueOk == false
                                    Tr({ style { property("border-bottom", "1px solid var(--rp-overlay)"); if (isDev) property("background", "var(--rp-err-bg)") } }) {
                                        Td({ style { rptCell(); property("font-weight", "600"); property("color", "var(--rp-subtle)") } }) { Text(formatReportDate(log.loggedAt)) }
                                        Td({ style { rptCell(); property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(log.templateName) }
                                        Td({ style { rptCell(); property("font-weight", "800"); property("color", if (isDev) "var(--rp-err-text)" else "var(--rp-ok-text)") } }) {
                                            Text(when {
                                                log.valueNumeric != null -> "${log.valueNumeric}°C"
                                                log.valueOk != null -> if (log.valueOk == true) "OK" else "Ej OK"
                                                else -> log.valueText ?: "—"
                                            })
                                        }
                                        Td({ style { rptCell(); property("font-weight", "600"); property("color", "var(--rp-text)") } }) { Text(log.loggedByName) }
                                        Td({ style { rptCell() } }) {
                                            if (isDev) {
                                                Span({ style { property("background", "var(--rp-err-bg)"); property("color", "var(--rp-love)"); property("padding", "0.15rem 0.5rem"); property("border-radius", "50px"); property("font-size", "0.75rem"); property("font-weight", "800") } }) {
                                                    Text("Avvikelse: ${log.deviationAction ?: "Ingen åtgärd"}")
                                                }
                                            } else {
                                                Span({ style { property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)"); property("padding", "0.15rem 0.5rem"); property("border-radius", "50px"); property("font-size", "0.75rem"); property("font-weight", "700") } }) {
                                                    Text("✓ Utförd")
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Print-Only Official Inspector Sign-off Footer
        Div({
            classes("print-only")
            style {
                property("margin-top", "2.5rem")
                property("padding-top", "1.25rem")
                property("border-top", "2px solid var(--rp-text)")
            }
        }) {
            Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "3rem") } }) {
                Div {
                    Div({ style { property("border-bottom", "1px solid var(--rp-text)"); property("height", "36px") } })
                    Div({ style { property("font-size", "0.82rem"); property("font-weight", "800"); property("margin-top", "0.35rem"); property("color", "var(--rp-text)") } }) { Text("Namnteckning — Verksamhetsansvarig (Café / Restaurang)") }
                    Div({ style { property("font-size", "0.75rem"); property("color", "var(--rp-text)"); property("margin-top", "0.15rem") } }) { Text("Datum & ort: _________________________________________") }
                }
                Div {
                    Div({ style { property("border-bottom", "1px solid var(--rp-text)"); property("height", "36px") } })
                    Div({ style { property("font-size", "0.82rem"); property("font-weight", "800"); property("margin-top", "0.35rem"); property("color", "var(--rp-text)") } }) { Text("Granskad & Godkänd av Livsmedelsinspektör") }
                    Div({ style { property("font-size", "0.75rem"); property("color", "var(--rp-text)"); property("margin-top", "0.15rem") } }) { Text("Datum & ort: _________________________________________") }
                }
            }
        }
    }
}

private fun StyleScope.denseCell() {
    property("padding", "0.35rem 0.5rem")
    property("border", "1px solid var(--rp-hl-med)")
    property("font-size", "0.78rem")
}



@Composable
private fun ReportKpiCard(label: String, value: String, color: String, subtext: String) {
    Div({
        style {
            property("background", "var(--rp-surface)")
            property("border-radius", "20px")
            property("padding", "1.25rem")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("gap", "0.3rem")
            property("border", "1px solid var(--rp-hl-med)")
            property("box-shadow", "0 4px 15px -2px rgba(0,0,0,0.03)")
        }
    }) {
        Span({ style { property("font-size", "2.1rem"); property("font-weight", "900"); property("color", color); property("line-height", "1.1") } }) { Text(value) }
        Span({ style { property("font-size", "0.75rem"); property("font-weight", "800"); property("color", "var(--rp-subtle)"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("text-align", "center") } }) { Text(label) }
        Span({ style { property("font-size", "0.72rem"); property("color", "var(--rp-muted)"); property("font-weight", "500") } }) { Text(subtext) }
    }
}

private fun todayReport(): String { val d = kotlin.js.Date(); return "${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}" }
private fun addReportDays(days: Int): String { val d = kotlin.js.Date(); (d.asDynamic()).setDate(d.getDate() + days); return "${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}" }
private fun formatReportDate(ms: Long): String { val d = kotlin.js.Date(ms.toDouble()); return "${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}" }

private fun org.jetbrains.compose.web.css.StyleScope.rptInput() { property("padding", "0.5rem 0.75rem"); property("border", "1.5px solid var(--rp-hl-med)"); property("border-radius", "12px"); property("font-size", "0.85rem"); property("font-weight", "600"); property("outline", "none") }
private fun org.jetbrains.compose.web.css.StyleScope.rptCell() { property("padding", "0.6rem 0.85rem"); property("border-bottom", "1px solid var(--rp-hl-med)") }
